/**
 * ADR-0223 — Atlas Collab: the served shapes (hub src/collab/types.ts) and
 * the small pieces every Collab page shares — the page shell with its three
 * route-link tabs, state tokens, face discs, the raised list, the chatter
 * thread. The console derives nothing: every line and date arrives served.
 */

import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { PageLayout } from "./PageLayout.js";
import { ApiRequestError } from "../lib/api.js";

// ── the served shapes ───────────────────────────────────────────────────────

export type IdeaState = "open" | "blocked" | "became" | "aside";
export type OccurrenceState = "recorded" | "next" | "scheduled" | "no_record";
export type PersonName = { name: string };
export type FaceT = { name: string; initials: string };

export type Occurrence = {
  kind: "series" | "one_off";
  series_id: string | null;
  session_id: string | null;
  name: string;
  date: string;
  time: string | null;
  state: OccurrenceState;
  line: string;
  colour_index: number | null;
  colour: string;
  href: string | null;
};

export type IdeaCard = {
  id: string;
  ref: string;
  title: string;
  state: IdeaState;
  state_label: string;
  state_line: string;
  quiet: boolean;
  quiet_since: string | null;
  raised_by: PersonName | null;
  raised_on: string;
  came_from: { kind: "session" | "direct"; session_id: string | null; label: string };
  discussed: number;
  chatter_count: number;
  redacted: boolean;
};

export type PathStep = { step: string; label: string; sub: string; mark: "done" | "now_blocked" | "now_became" | "now_aside" | "todo" };
export type Blocker = { id: string; kind: "issue" | "action_item"; ref_id: string; title: string; status: string; status_label: string; settled: boolean; assignee: PersonName | null; href: string; line: string };
export type Outcome = { kind: string; ref: string; label: string; href: string | null };
export type Message = {
  id: string;
  author: FaceT | null;
  body: string;
  at: string;
  posted_via: "console" | "door";
  via_line: string | null;
  edited: boolean;
  redacted: { at: string } | null;
  mine: boolean;
  can: { edit: boolean; redact: boolean };
};

export type Idea = IdeaCard & {
  description: string | null;
  raised_at: string;
  path: PathStep[];
  blockers: Blocker[];
  outcome: Outcome | null;
  set_aside: { reason: string; at: string; by: PersonName | null } | null;
  sessions: Array<{ id: string; label: string; date: string }>;
  chatter: Message[];
  visibility_line: string;
  can: { edit: boolean; make: boolean; set_aside: boolean; reopen: boolean; link_blocker: boolean; redact: boolean };
};

export type RaisedRow = {
  item_id: string | null;
  position: number;
  kind: "idea" | "issue" | "action_item";
  ref_id: string;
  title: string;
  href: string;
  opens_in: string | null;
  raised_by: PersonName | null;
  went: { token: string; label: string; line: string };
};
export type DecisionRow = { item_id: string; position: number; text: string; people: PersonName[]; attribution: string; initiative: { id: string; ref: string; name: string; href: string } | null };
export type LearningRow = { item_id: string; position: number; text: string; tree: "knowledge" | "guidance"; people: PersonName[]; state: "pending" | "accepted" | "gone"; line: string; entry_href: string | null };
export type NoteRow = { item_id: string; position: number; text: string; people: PersonName[] };

export type SeriesSummary = {
  id: string;
  name: string;
  purpose: string | null;
  weekday: number;
  start_time: string;
  duration_minutes: number | null;
  timezone: string;
  starts_on: string;
  ended_on: string | null;
  colour_index: number;
  colour: string;
  when_line: string;
  meets_line: string;
  members: Array<{ account_id: string; name: string }>;
};
export type SeriesCard = SeriesSummary & {
  href: string;
  next: { date: string; day_number: string; label: string; agenda_count: number; line: string; href: string } | null;
  last: { session_id: string; held_on: string; decided: number; raised: number; line: string } | null;
  can: { edit: boolean; record: boolean };
};
export type SessionAgenda = { still_open: RaisedRow[]; new_since: RaisedRow[]; empty_line: string | null; from_label: string | null };
export type Session = {
  id: string;
  kind: "series" | "one_off";
  series: SeriesSummary | null;
  title: string;
  held_on: string;
  date_block: { dow: string; day: string; month_year: string };
  date_label: string;
  starts_at: string | null;
  duration_minutes: number | null;
  location: string | null;
  meta_line: string | null;
  recorded: boolean;
  recorded_at: string | null;
  recorded_by: PersonName | null;
  attendees: FaceT[];
  attended_line: string | null;
  decisions: DecisionRow[];
  raised: RaisedRow[];
  learned: LearningRow[];
  notes: NoteRow[];
  carry: { count: number; next_date: string; line: string; href: string } | null;
  chatter: Message[];
  about_line: string;
  can: { edit: boolean };
  attendee_account_ids: string[];
  learning_vocab: { knowledge_types: string[]; guidance_categories: string[] };
};
export type OccurrencePage = {
  occurrence: Occurrence;
  series: SeriesSummary;
  agenda: SessionAgenda;
  last_session: { id: string; held_on: string; label: string } | null;
  date_block: { dow: string; day: string; month_year: string };
  date_label: string;
  chatter: Message[];
  can: { record: boolean };
  member_account_ids: string[];
  learning_vocab: { knowledge_types: string[]; guidance_categories: string[] };
};
export type HeldItem = { kind: "issue" | "action_item"; ref_id: string; title: string; status_label: string; href: string };
export type Holding = { person: PersonName | null; count: number; count_line: string; items: HeldItem[] };
export type DecidedRow = { text: string; session_id: string; where: string; href: string };
export type WeekDay = { date: string; weekday: string; day_number: string; today: boolean; meetings: Occurrence[]; empty_line: string | null };
export type Week = {
  start: string;
  end: string;
  kicker: string;
  days: WeekDay[];
  ideas: Record<IdeaState, { label: string; count: number; items: IdeaCard[] }>;
  decided: DecidedRow[];
  holding: Holding[];
};
export type CalendarMonth = { month: string; label: string; days: Array<{ date: string; meetings: Occurrence[] }>; legend: Array<{ name: string; colour: string }> };
export type OneOffRow = { session_id: string; held_on: string; date_label: string; time: string | null; title: string; summary: string | null; recorded: boolean; line: string; href: string };
export type SeriesPage = {
  series: SeriesCard;
  sessions: Array<{ id: string; held_on: string; date_label: string; decided: number; raised: number; learned: number; noted: number; attended: number; line: string; href: string }>;
  missed: Array<{ date: string; label: string; href: string }>;
  can: { edit: boolean; record: boolean };
};
export type OneOffMonth = { month: string; label: string; sessions: OneOffRow[] };

export const IDEA_STATES: IdeaState[] = ["open", "blocked", "became", "aside"];

export function errMessage(e: unknown): string {
  return e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
}

/** The hub's `{ error, message, reason, duplicate }` body, when the error carries one. */
export function errBody(e: unknown): { error?: string; reason?: string; duplicate?: { id: string; ref: string; title: string } } | null {
  const d = (e as { data?: unknown })?.data;
  return d && typeof d === "object" ? (d as { error?: string; reason?: string; duplicate?: { id: string; ref: string; title: string } }) : null;
}

// ── the shell: title, New idea, the three tabs ──────────────────────────────

const TABS: Array<{ to: string; label: string; match: (p: string) => boolean }> = [
  { to: "/app/collab", label: "This week", match: (p) => p === "/app/collab" || p === "/app/collab/" },
  { to: "/app/collab/ideas", label: "Ideas", match: (p) => p.startsWith("/app/collab/ideas") },
  { to: "/app/collab/meetings", label: "Meetings", match: (p) => p.startsWith("/app/collab/meetings") || p.startsWith("/app/collab/series") || p.startsWith("/app/collab/sessions") },
];

export function CollabTabs(props: { pathname: string }) {
  return (
    <nav className="collabTabRow" aria-label="Collab">
      {TABS.map((t) => (
        <Link key={t.to} to={t.to} className={`collabTab${t.match(props.pathname) ? " collabTabOn" : ""}`}>
          {t.label}
        </Link>
      ))}
    </nav>
  );
}

/** Every Collab page: PageLayout with the title, New idea in the actions slot, the tab row, then the Collab block. */
export function CollabPage(props: { title: ReactNode; crumb?: ReactNode; children: ReactNode; noNewIdea?: boolean }) {
  const { pathname } = useLocation();
  return (
    <PageLayout
      title={props.title}
      breadcrumb={props.crumb}
      actions={
        props.noNewIdea ? undefined : (
          <Link className="btn btnPrimary" to="/app/collab/ideas/new">
            New idea
          </Link>
        )
      }
    >
      <CollabTabs pathname={pathname} />
      <div className="collab" style={{ marginTop: 20 }}>
        {props.children}
      </div>
    </PageLayout>
  );
}

// ── tokens, faces ───────────────────────────────────────────────────────────

const TOK_CLASS: Record<string, string> = {
  open: "collabTokOpen",
  blocked: "collabTokBlocked",
  became: "collabTokBecame",
  aside: "collabTokAside",
  recorded: "collabTokHeld",
  next: "collabTokNext",
  scheduled: "collabTokNext",
  no_record: "collabTokNone",
  // the referenced objects' own statuses
  resolved: "collabTokBecame",
  closed: "collabTokBecame",
  dismissed: "collabTokAside",
  acknowledged: "collabTokOpen",
  in_progress: "collabTokOpen",
  deferred: "collabTokAside",
};

export function Tok(props: { token: string; label: string }) {
  return <span className={`collabTok ${TOK_CLASS[props.token] ?? "collabTokHeld"}`}>{props.label}</span>;
}

const FACE_COLOURS = ["#1D5C8A", "#4E7CA1", "#7A5C99", "#B5651D", "#2F7A55", "#132A44"];
function faceColour(name: string): string {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return FACE_COLOURS[h % FACE_COLOURS.length]!;
}

export function Face(props: { face: FaceT; size?: number }) {
  const s = props.size ?? 28;
  return (
    <span className="collabFace" style={{ background: faceColour(props.face.name), width: s, height: s, fontSize: Math.round(s * 0.4) }} title={props.face.name}>
      {props.face.initials}
    </span>
  );
}

export function Faces(props: { faces: FaceT[] }) {
  return (
    <span className="collabFaces">
      {props.faces.map((f) => (
        <Face key={f.name} face={f} />
      ))}
    </span>
  );
}

// ── the raised list (§3.5) ──────────────────────────────────────────────────

export function RaisedList(props: { rows: RaisedRow[]; onRemove?: (row: RaisedRow) => void; action?: { label: string; onClick: (row: RaisedRow) => void } }) {
  return (
    <ol className="collabRaised">
      {props.rows.map((r) => (
        <li key={`${r.kind}:${r.ref_id}`}>
          <div>
            <Link className="collabRTitle" to={r.href}>
              {r.title}
            </Link>
            <div className="collabRWho">
              {r.raised_by ? `Raised by ${r.raised_by.name}` : ""}
              {r.raised_by && r.opens_in ? " · " : ""}
              {r.opens_in ?? ""}
            </div>
            <div className="collabWent">
              <span className="collabArrow">→</span>
              <Tok token={r.went.token} label={r.went.label} />
              <span>{r.went.line}</span>
              {props.onRemove ? (
                <button type="button" className="collabLinkBtn isDanger" onClick={() => props.onRemove!(r)}>
                  Remove
                </button>
              ) : null}
              {props.action ? (
                <button type="button" className="collabLinkBtn" onClick={() => props.action!.onClick(r)}>
                  {props.action.label}
                </button>
              ) : null}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

// ── chatter (§3.6) ──────────────────────────────────────────────────────────

export function Thread(props: {
  messages: Message[];
  prompt: string;
  onPost: (body: string) => Promise<void>;
  onEdit?: (id: string, body: string) => Promise<void>;
  onRedact?: (id: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  async function post() {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await props.onPost(body);
      setDraft("");
    } finally {
      setBusy(false);
    }
  }
  async function saveEdit() {
    if (!editing || !props.onEdit) return;
    const body = editing.body.trim();
    if (!body) return;
    setBusy(true);
    try {
      await props.onEdit(editing.id, body);
      setEditing(null);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="collabThread">
      {props.messages.length === 0 ? <div className="collabEmpty">Nothing yet.</div> : null}
      {props.messages.map((m) => (
        <div className="collabMsg" key={m.id}>
          {m.author ? <Face face={m.author} /> : <span className="collabFace" style={{ background: "#C3CCD6" }}>?</span>}
          <div>
            <span className="collabMsgWho">
              {m.author?.name ?? "A former member"}
              <span className="collabMsgWhen">{m.at}</span>
              {m.edited ? <span className="collabMsgWhen">edited</span> : null}
            </span>
            {editing?.id === m.id ? (
              <div>
                <textarea className="collabInput" value={editing.body} onChange={(e) => setEditing({ id: m.id, body: e.target.value })} maxLength={4000} aria-label="Edit your message" />
                <div className="collabPost">
                  <button type="button" className="collabBtn" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                  <button type="button" className="collabBtn collabBtnAmber" disabled={busy || !editing.body.trim()} onClick={() => void saveEdit()}>
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <p className="collabMsgText" style={m.redacted ? { color: "var(--collab-faint)", fontStyle: "italic" } : undefined}>
                {m.body}
                {m.redacted ? ` · ${m.redacted.at}` : ""}
              </p>
            )}
            {m.via_line ? <div className="collabMsgVia">{m.via_line}</div> : null}
            {(m.can.edit && props.onEdit) || (m.can.redact && props.onRedact) ? (
              <div className="collabMsgActions">
                {m.can.edit && props.onEdit && editing?.id !== m.id ? (
                  <button type="button" className="collabLinkBtn" onClick={() => setEditing({ id: m.id, body: m.body })}>
                    Edit
                  </button>
                ) : null}
                {m.can.redact && props.onRedact ? (
                  <button type="button" className="collabLinkBtn isDanger" onClick={() => void props.onRedact!(m.id)}>
                    Redact
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ))}
      <div>
        <textarea className="collabInput" aria-label="Add to the chatter" placeholder={props.prompt} value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={4000} />
        <div className="collabPost">
          <button type="button" className="collabBtn" disabled={busy || !draft.trim()} onClick={() => void post()}>
            Post
          </button>
        </div>
      </div>
    </div>
  );
}

// ── the week tile and the idea card ─────────────────────────────────────────

export function MeetingTile(props: { o: Occurrence }) {
  const { o } = props;
  const cls = `collabMtg${o.state === "recorded" ? " collabMtgHeld" : o.state === "no_record" ? " collabMtgNone" : ""}`;
  const tokLabel = o.state === "recorded" ? "Recorded" : o.state === "no_record" ? "No record" : "Upcoming";
  const inner = (
    <>
      <span className="collabMtgTime">{o.time ?? ""}</span>
      <span className="collabMtgName">{o.name}</span>
      <span className="collabMtgLine">{o.line}</span>
      <Tok token={o.state} label={tokLabel} />
    </>
  );
  return o.href ? (
    <Link className={cls} to={o.href}>
      {inner}
    </Link>
  ) : (
    <span className={cls}>{inner}</span>
  );
}

export function IdeaCardView(props: { card: IdeaCard }) {
  const c = props.card;
  return (
    <Link className="collabCard" to={`/app/collab/ideas/${c.id}`}>
      <span className="collabCardTitle">{c.title}</span>
      <span className="collabCardMeta">
        <span>
          {c.raised_by?.name ?? "—"} · {shortDate(c.raised_on)}
        </span>
        {c.quiet ? <span className="collabQuiet">Quiet</span> : null}
      </span>
      <span className="collabCardNote">
        <span className="collabArrow">→</span>
        <span>{c.state_line}</span>
      </span>
    </Link>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** The one date the card shows (*Nov 3*) from the served YYYY-MM-DD, with no timezone shift. */
export function shortDate(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : ymd;
}

/** The four columns (§3.2, §3.3): `limit` cards per column and a link for the rest. */
export function Board(props: { ideas: Week["ideas"]; limit: number; more: (state: IdeaState, count: number) => ReactNode }) {
  return (
    <div className="collabFlow">
      {IDEA_STATES.map((s) => {
        const col = props.ideas[s];
        const stageCls = s === "open" ? "collabStageOpen" : s === "blocked" ? "collabStageBlocked" : s === "became" ? "collabStageBecame" : "";
        return (
          <div className={`collabStage ${stageCls}`} key={s}>
            <div className="collabStageHead">
              <b>{col.label}</b>
              <span className="num">{col.count}</span>
            </div>
            {col.items.length === 0 ? <div className="collabEmpty">None</div> : col.items.slice(0, props.limit).map((c) => <IdeaCardView key={c.id} card={c} />)}
            {col.count > props.limit ? props.more(s, col.count) : null}
          </div>
        );
      })}
    </div>
  );
}
